import React, {
  Children,
  useState,
  useEffect,
  useRef,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { Box, Flex, css } from 'theme-ui';
import Divider from '@solid-ui-components/Divider';
import { TabsContext } from '@solid-ui-components/Tabs';
import styles from './styles';
import ArrowIcon from '@solid-ui-components/Icons/ArrowIcon';

const StyledTabs = ({
  id,
  tabs,
  children,
  variant,
  space,
  position,
  autoplay,
  autoplayInterval,
  arrows,
  onChange,
  forceRender,
  tabsToSwipe = 1,
  showItem = true,
  showTabsList = true,
}) => {
  const { activeTab, setActiveTab } = useContext(TabsContext);
  const [tabIndex, setTabIndex] = useState(0);
  const interval = useRef(null);

  const selectedIndex = id ? activeTab?.index || 0 : tabIndex;
  const childrenArray = Children.toArray(children);
  const totalTabs = tabs?.length || childrenArray?.length;
  const totalPages = totalTabs / tabsToSwipe;
  const currentPage = Math.ceil((selectedIndex + 1) / tabsToSwipe);

  const tabIndexToSet =
    tabIndex < totalTabs - tabsToSwipe ? tabIndex + tabsToSwipe : 0;

  useEffect(() => {
    interval.current =
      autoplay &&
      setInterval(() => {
        setTabIndex(tabIndexToSet);
      }, autoplayInterval);
    return () => clearInterval(interval.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () =>
      id && setActiveTab((state) => (state.identifier === id ? {} : state));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = (index) => {
    clearInterval(interval.current);
    id ? setActiveTab({ identifier: id, index }) : setTabIndex(index);
    onChange && onChange(index);
  };

  const handleNext = () => {
    setTabIndex(tabIndexToSet);
  };

  const handlePrev = () => {
    setTabIndex((tabIndex) =>
      tabIndex !== 0 ? tabIndex - tabsToSwipe : totalTabs - tabsToSwipe
    );
  };

  const customTabButtons = () =>
    tabs.filter(Boolean).map((item, index) => (
      <Tab
        key={`item-${index}`}
        className='tabs_tab'
        style={{ display: showTabsList ? 'block' : 'none' }}
      >
        {item}
      </Tab>
    ));

  const contentTabButtons = () =>
    childrenArray.map(({ props }, index) =>
      variant === 'dots-new' ? (
        <Tab
          key={`item-${index}`}
          className='tabs_tab'
          style={{
            ...(index % tabsToSwipe !== 0 ? { display: 'none' } : {}),
            ...(index === selectedIndex ? {} : {}),
          }}
        />
      ) : variant === 'dots' ? (
        <Tab key={`item-${index}`} className='tabs_tab' />
      ) : (
        props?.content?.text?.[0] && (
          <Tab key={`item-${index}`} className='tabs_tab'>
            {props.content.text[0].text}
          </Tab>
        )
      )
    );

  const tabButtons = (
    <TabList className='tabs_tabList'>
      {tabs ? customTabButtons() : contentTabButtons()}
    </TabList>
  );

  return totalTabs ? (
    <Box sx={styles[variant]}>
      {arrows && (
        <Box sx={styles.arrowWrapper}>
          <Box sx={styles.arrowButton} onClick={handlePrev}>
            <ArrowIcon />
          </Box>
          <Box sx={styles.pages}>
            {currentPage} of {totalPages}
          </Box>
          <Box sx={styles.arrowButton} onClick={handleNext}>
            <ArrowIcon style={{ rotate: '180deg' }} />
          </Box>
        </Box>
      )}
      <Tabs
        selectedTabClassName='tabs_selectedTab'
        selectedIndex={selectedIndex}
        onSelect={handleSelect}
      >
        {position === 'top' && <>{tabButtons}</>}
        {childrenArray.map((item, index) => (
          <TabPanel key={`item-${index}`} forceRender={forceRender}>
            {showItem && variant === 'dots-new' ? (
              <Flex sx={styles.dotsNewArrowContainer}>
                <div
                  onClick={handlePrev}
                  css={css({
                    ...styles.gradientArrowButtonContainer,
                    marginRight: '40px',
                    display: ['flex', 'none', 'flex', 'flex', 'flex'],
                  })}
                >
                  <div css={css(styles.gradientArrowButton)}>
                    <ArrowIcon style={styles.leftArrow} color='#026AA2' />
                  </div>
                </div>
                {item}
                <div
                  onClick={handleNext}
                  css={css({
                    ...styles.gradientArrowButtonContainer,
                    marginLeft: '40px',
                    display: ['flex', 'none', 'flex', 'flex', 'flex'],
                  })}
                >
                  <div css={css(styles.gradientArrowButton)}>
                    <ArrowIcon color='#026AA2' style={styles.rightArrow} />
                  </div>
                </div>
              </Flex>
            ) : (
              item
            )}
          </TabPanel>
        ))}
        {position === 'bottom' && (
          <>
            <Flex sx={{ flex: 1, height: '24px' }} />
            {tabButtons}
          </>
        )}
      </Tabs>
    </Box>
  ) : null;
};

export default StyledTabs;

StyledTabs.defaultProps = {
  variant: 'underline',
  position: 'top',
  autoplay: false,
  autoplayInterval: 4000,
  arrows: false,
  forceRender: false,
};

StyledTabs.propTypes = {
  variant: PropTypes.oneOf(Object.keys(styles)),
  tabs: PropTypes.array,
};
